import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Img } from "../motion/Reveal";
import { SplitWords, prefersReducedMotion } from "../motion/Kinetic";
import { isEn, t } from "../../lib/i18n";

/** Mirrors the seeded site settings so the hero can paint before /api/home answers – without a text swap afterwards. */
export const HERO_DEFAULTS = {
  hero_kicker: t("Lokalt ledet. Langsiktig eid."),
  hero_headline: t("EQVA bygger sterke industriselskaper"),
  hero_subline: t("EQVA samler spesialiserte selskaper innen engineering, fabrikasjon, elektro, rør og vedlikehold – tett på norsk industri."),
  hero_poster: "/images/hero-bks-poster.jpg",
  hero_video_url: "/video/hero-bks-22.mp4",
  stats: [
    { key: "people", value: 750, decimals: 0, prefix: "", suffix: "+", label: t("mennesker") },
    { key: "companies", value: 10, decimals: 0, prefix: "", suffix: "", label: t("selskaper") },
    { key: "backlog", value: 1.1, decimals: 1, prefix: isEn ? "NOK " : "", suffix: isEn ? " bn" : " mrd.", label: t("ordrereserve") },
  ],
};

/** Scroll distance over which the film opens from its resting height to its cinematic height. */
const GROW_DISTANCE = 320;
/** Easing time constant for the scroll-linked growth (ms). */
const GROW_EASE_MS = 150;

/** Only fetch the loop when the connection can afford it. */
const canPlayVideo = () => {
  if (typeof navigator === "undefined") return false;
  const c = navigator.connection;
  if (c?.saveData) return false;
  if (c?.effectiveType && /2g|3g/.test(c.effectiveType)) return false;
  return !prefersReducedMotion();
};

/** Three sizes of the same film – 640 px (~1 MB) for phones, 960 px for laptops under 1280 CSS px, 1280 px above –
 *  each as H.264 (every phone, hardware-decoded) with a VP9/WebM twin for browsers built without H.264. */
const videoSources = (url) => {
  if (!url) return [];
  if (!/\.mp4$/.test(url)) return [{ src: url, type: "video/mp4" }];
  const w = typeof window === "undefined" ? 1920 : window.innerWidth;
  const size = w < 768 ? 640 : w < 1280 ? 960 : 1280;
  const base = url.replace(/\.mp4$/, "");
  return [
    { src: size === 1280 ? url : `${base}-${size}.mp4`, type: "video/mp4" },
    { src: `${base}-${size}.webm`, type: "video/webm" },
  ];
};

/**
 * Hero – one sentence on white, then the film.
 * The headline stands alone; below it an editorial grid: the brand line ("Lokalt ledet. Langsiktig eid.") in
 * its own narrow column, the explanation and two clearly different actions in a wide one. No figures, no
 * rules – the white space on the right is deliberate. Generous vertical air (~70 / 60 / 70 px on a laptop)
 * gives the minimalism its calm. The film runs edge to edge at a real hero height (~32vw, max 500px), sits
 * over its own poster, loads only after everything else (640/960/1280 px encode by viewport) and pauses when it scrolls out of view.
 */
export const Hero = ({ settings = {} }) => {
  const s = { ...HERO_DEFAULTS, ...settings };
  const [on, setOn] = useState(false);
  const [mountVideo, setMountVideo] = useState(false);
  const [videoOk, setVideoOk] = useState(true);
  const [videoReady, setVideoReady] = useState(false);
  const mediaRef = useRef(null);
  const videoRef = useRef(null);

  useEffect(() => {
    const t = requestAnimationFrame(() => setOn(true));
    return () => cancelAnimationFrame(t);
  }, []);

  // Defer the loop: decent connection, after load + a beat so the poster, fonts and data win. Phones get the 640 px encode.
  useEffect(() => {
    if (!s.hero_video_url || !canPlayVideo()) return;
    let t;
    const go = () => { t = setTimeout(() => { if (canPlayVideo()) setMountVideo(true); }, 1200); };
    if (document.readyState === "complete") go(); else window.addEventListener("load", go, { once: true });
    return () => { clearTimeout(t); window.removeEventListener("load", go); };
  }, [s.hero_video_url]);

  // Never decode frames nobody sees: pause when the film leaves the viewport or the tab is hidden.
  useEffect(() => {
    if (!mountVideo) return;
    const media = mediaRef.current;
    const v = videoRef.current;
    if (!media || !v) return;
    // React sets `muted` as a property only; iOS/Chrome autoplay policy wants the real muted state, so pin it on the node.
    v.muted = true; v.defaultMuted = true;
    let visible = true;
    const sync = () => {
      const should = visible && document.visibilityState === "visible";
      if (should) v.play().catch(() => {}); else v.pause();
    };
    const io = new IntersectionObserver(([e]) => { visible = e.isIntersecting; sync(); }, { threshold: 0.05 });
    io.observe(media);
    document.addEventListener("visibilitychange", sync);
    // Low Power Mode / data-saver browsers refuse autoplay: retry once on the first real gesture.
    const gesture = () => { if (v.paused && visible) v.play().catch(() => {}); };
    const gestureOpts = { once: true, passive: true };
    window.addEventListener("touchend", gesture, gestureOpts);
    window.addEventListener("pointerup", gesture, gestureOpts);
    return () => {
      io.disconnect();
      document.removeEventListener("visibilitychange", sync);
      window.removeEventListener("touchend", gesture);
      window.removeEventListener("pointerup", gesture);
    };
  }, [mountVideo]);

  // The frame opens as you scroll (desktop, motion allowed): --grow 0→1 over the first ~320px.
  // Scroll only sets a target; a rAF loop eases the value towards it with a time constant (~150 ms), so
  // wheel steps, trackpads and 120 Hz displays all produce the same continuous motion. One custom
  // property is written per frame; the bitmap itself is never re-laid out (see .hero-media in index.css).
  useEffect(() => {
    const el = mediaRef.current;
    if (!el || window.innerWidth < 1024 || prefersReducedMotion()) return;
    let target = 0, current = 0, raf = 0, prev = 0;
    const write = (v) => el.style.setProperty("--grow", v.toFixed(4));
    const step = (t) => {
      const dt = prev ? Math.min(64, t - prev) : 16.7;
      prev = t;
      const k = 1 - Math.exp(-dt / GROW_EASE_MS);
      current += (target - current) * k;
      if (Math.abs(target - current) < 0.001) { current = target; raf = 0; prev = 0; }
      else raf = requestAnimationFrame(step);
      write(current);
    };
    const onScroll = () => {
      target = Math.min(1, Math.max(0, window.scrollY / GROW_DISTANCE));
      if (!raf) raf = requestAnimationFrame(step);
    };
    // Reloads mid-page: land on the final value without easing.
    current = target = Math.min(1, Math.max(0, window.scrollY / GROW_DISTANCE));
    write(current);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => { window.removeEventListener("scroll", onScroll); if (raf) cancelAnimationFrame(raf); };
  }, []);

  const headline = String(s.hero_headline || "").replace(/\*/g, "");
  const brandLines = String(s.hero_kicker || "").split(/\.\s+/).filter(Boolean).map((l) => (l.endsWith(".") ? l : `${l}.`));
  const rise = (d) => ({ "--d": `${d}ms` });
  const riseCls = `rise ${on ? "is-in" : ""}`;

  return (
    <section data-testid="floor-hero" className="relative flex min-h-[100svh] flex-col bg-white text-ink">
      {/* Air is the design: navbar → headline ~70px, headline → grid ~60px, CTA → film ~70px on a laptop */}
      <div className="wrap pt-[calc(var(--header-h)+clamp(36px,8vh,76px))]">
        <h1 data-testid="hero-headline" className="t-hero text-navy max-w-[18ch]">
          <SplitWords text={headline} on={on} delay={80} step={70} />
        </h1>

        {/* Editorial grid: the brand line in a narrow column, the explanation and the one action in a wide one */}
        <div style={rise(480)} className={`${riseCls} mt-[clamp(40px,8.2vh,78px)] grid-12 gap-y-5 items-start`}>
          <p data-testid="hero-kicker" className="t-hero-brand text-navy col-span-12 lg:col-span-3">
            {brandLines.map((l, i) => <span key={i} className="block">{l}</span>)}
          </p>
          <div className="col-span-12 lg:col-span-7 lg:col-start-4 max-w-[700px]">
            <p data-testid="hero-subline" className="t-hero-copy text-[rgba(13,17,23,0.7)]">{s.hero_subline}</p>
            <div className="mt-6">
              <Link to="/selskaper" data-testid="hero-cta-selskaper" className="btn-pill bg-navy text-white hover:bg-blue">{t("Møt selskapene")} <ArrowRight size={16} /></Link>
            </div>
          </div>
        </div>
      </div>

      {/* Edge-to-edge film at hero height; closes the first screen on tall displays */}
      <div ref={mediaRef} data-testid="hero-media" className="hero-media relative mt-[clamp(44px,8vh,72px)] flex-1 overflow-hidden bg-surface">
        <Img
          src={s.hero_poster}
          alt={t("Sveiser i arbeid hos BKS Industri")}
          eager
          fade={false}
          fetchPriority="high"
          sizes="100vw"
          widths={[480, 800, 1200, 1920]}
          className={`hero-media-img absolute inset-0 h-full w-full object-cover ${on ? "is-in" : ""}`}
        />
        {mountVideo && (
          <video
            ref={videoRef}
            data-testid="hero-video"
            data-video-state={!videoOk ? "error" : videoReady ? "playing" : "loading"}
            className={`absolute inset-0 h-full w-full object-cover transition-opacity duration-700 ${videoOk && videoReady ? "opacity-100" : "opacity-0"}`}
            autoPlay muted loop playsInline preload="auto"
            aria-hidden="true"
            onPlaying={() => setVideoReady(true)}
            onError={(e) => { if (e.target === e.currentTarget) setVideoOk(false); }}  /* a <source> that fails is not the film failing – the browser moves on to the next one */
          >
            {videoSources(s.hero_video_url).map((v, i, all) => (
              /* The browser walks the list; only the LAST source failing means the film cannot play here */
              <source key={v.src} src={v.src} type={v.type} onError={i === all.length - 1 ? () => setVideoOk(false) : undefined} />
            ))}
          </video>
        )}
        <span aria-hidden="true" className="hero-veil" />
      </div>
    </section>
  );
};
